import type { ReactNode } from 'react';
import { T } from 'gt-react';
import type { WorkbookSheetKind } from '@erp/shared-types';

/** Every Sheet kind, in the order Add Worksheet offers them. */
export const SHEET_KINDS = [
  'columnar',
  'pivot',
  'freeform',
] as const satisfies readonly WorkbookSheetKind[];

/** The tab that adds a Worksheet of one kind; null: it is added at once, reading nothing yet. */
export type WorkbookAddSheetTab = 'add-worksheet' | 'add-pivot';

/**
 * What the editor shows of each Sheet kind before any Worksheet of it exists:
 * its Add Worksheet menu entry, the tab that adds one, and the word the
 * workspace agent reads for it. A kind missing here fails to compile. What a
 * Worksheet of a kind renders (its panel, its Process details, its step
 * description) dispatches with `matchSheetKind`, which is exhaustive too.
 */
export const SHEET_KIND_UI: Record<
  WorkbookSheetKind,
  {
    title: () => ReactNode;
    description: () => ReactNode;
    addTab: WorkbookAddSheetTab | null;
    agentLabel: string;
  }
> = {
  columnar: {
    title: () => <T context="workbook sheet kind">Columnar sheet</T>,
    description: () => (
      <T context="workbook columnar sheet description">
        Input columns and formula columns, calculated row by row.
      </T>
    ),
    addTab: 'add-worksheet',
    agentLabel: 'worksheet',
  },
  pivot: {
    title: () => <T context="workbook sheet kind">Pivot table</T>,
    description: () => (
      <T context="workbook pivot sheet description">
        Groups another table&apos;s rows and sums, counts or averages them.
      </T>
    ),
    addTab: 'add-pivot',
    agentLabel: 'pivot worksheet',
  },
  freeform: {
    title: () => <T context="workbook sheet kind">Freeform sheet</T>,
    description: () => (
      <T context="workbook freeform sheet description">
        A spreadsheet grid where any cell can reference a single cell, such as
        A4, or a range of another source or worksheet.
      </T>
    ),
    addTab: null,
    agentLabel: 'freeform worksheet',
  },
};
